import {
  useMutation,
  type UseMutationOptions,
} from '@tanstack/react-query';

import { useVideaNotesApi } from '../context';

import type { SubmitWritebackMutationVariables } from './types';

type SubmitWritebackMutationOptions<
  TVariables extends SubmitWritebackMutationVariables,
> = Omit<UseMutationOptions<void, Error, TVariables>, 'mutationFn'> & {
  getQueryParams?: (variables: TVariables) => Record<string, string> | undefined;
};

export const useSubmitWritebackMutation = <
  TVariables extends SubmitWritebackMutationVariables = SubmitWritebackMutationVariables,
>(
  options?: SubmitWritebackMutationOptions<TVariables>,
) => {
  const { submitWriteback } = useVideaNotesApi();
  const { getQueryParams, ...mutationOptions } = options ?? {};

  return useMutation<void, Error, TVariables>({
    // Writeback uses POST, so avoid automatic retries by default to prevent
    // duplicate submissions when the server succeeds but the client misses
    // the response. Callers can still opt in with an explicit retry option.
    retry: mutationOptions.retry ?? false,
    ...mutationOptions,
    mutationFn: async (variables) => {
      const queryParams = getQueryParams
        ? getQueryParams(variables)
        : variables.queryParams;
      const result = await submitWriteback(
        variables.clinicalNoteId,
        queryParams,
      );

      if (!result.success) {
        throw new Error(result.error ?? 'Writeback failed');
      }
    },
  });
};
